import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'
import { useAdminISODeals } from '../hooks/useAdminISODeals'
import '../styles/AdminDashboard.css'

interface Submission {
  id: string
  lenderId: string
  lenderName?: string
  status: string
  createdDate: string
}

interface Opportunity {
  id: string
  name: string
  businessLegalName?: string
  amount: number
  closeDate?: string
  createdDate: string
  submissions: Submission[]
}

const ISODetailPage: React.FC = () => {
  const { isoId } = useParams<{ isoId: string }>()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set())
  
  const { data: dealsData, loading, error, refetch } = useAdminISODeals(isoId || '')

  // Debug logging
  console.log('🔍 ISO Detail Page Debug:', {
    isoId,
    dealsData,
    loading,
    error,
    timestamp: new Date().toISOString()
  })

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getSubmissionStatusBadge = (status: string) => {
    // Normalize status to lowercase for matching
    const normalizedStatus = status?.toLowerCase().trim()
    
    const statusConfig = {
      'submitted': { class: 'bg-primary text-white', text: 'Submitted' },
      'approved': { class: 'bg-success text-white', text: 'Approved' },
      'declined': { class: 'bg-danger text-white', text: 'Declined' },
      'pending': { class: 'bg-warning text-dark', text: 'Pending' },
      'under_review': { class: 'bg-info text-white', text: 'Under Review' },
      'withdrawn': { class: 'bg-secondary text-white', text: 'Withdrawn' }
    }
    
    const config = statusConfig[normalizedStatus as keyof typeof statusConfig] || { 
      class: 'bg-primary text-white', 
      text: status || 'Submitted' 
    }
    
    return (
      <span className={`badge ${config.class}`} style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}>
        {config.text}
      </span>
    )
  }

  const handleLogout = async () => {
    await logout()
  }

  const toggleDeal = (dealId: string) => {
    const newExpanded = new Set(expandedDeals)
    if (newExpanded.has(dealId)) {
      newExpanded.delete(dealId)
    } else {
      newExpanded.add(dealId)
    }
    setExpandedDeals(newExpanded)
  }

  return (
    <div className="admin-dashboard">
      {/* Left Sidebar Navigation */}
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-brand">
            <span className="admin-brand-icon">🛡️</span>
            <span className="admin-brand-text">Admin Portal</span>
          </div>
        </div>
        
        <nav className="admin-sidebar-nav">
          <ul className="admin-nav-list">
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-people admin-nav-icon"></i>
                <span className="admin-nav-text">Users</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-envelope-plus admin-nav-icon"></i>
                <span className="admin-nav-text">Invitations</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button active"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-building admin-nav-icon"></i>
                <span className="admin-nav-text">ISOs</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-envelope-gear admin-nav-icon"></i>
                <span className="admin-nav-text">Notification Settings</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-gear-fill admin-nav-icon"></i>
                <span className="admin-nav-text">ISO Settings</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className="admin-nav-button"
                onClick={() => navigate('/admin-dashboard')}
                type="button"
              >
                <i className="bi bi-diagram-3 admin-nav-icon"></i>
                <span className="admin-nav-text">Field Mappings</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <div className="admin-user-name">{user?.name}</div>
            <div className="admin-user-role">Administrator</div>
          </div>
          <button 
            className="admin-logout-button"
            onClick={handleLogout}
            type="button"
          >
            <i className="bi bi-box-arrow-right"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="admin-main-content">
        <div className="admin-content-wrapper">
          <div className="admin-content-section">

            {loading && (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted">Loading ISO details...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {!loading && !error && !dealsData && (
              <div className="alert alert-warning" role="alert">
                No data available for this ISO.
              </div>
            )}

            {!loading && !error && dealsData && (
              <>
                {/* Header */}
                <div className="admin-content-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <button 
                        className="btn btn-primary btn-sm mb-3" 
                        onClick={() => navigate('/admin-dashboard?tab=isos')}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Back to ISOs
                      </button>
                      <h2 className="admin-page-title mb-2">
                        {dealsData.isoInfo?.companyName || 'ISO Details'}
                      </h2>
                      <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                        <i className="bi bi-person me-1"></i>
                        {dealsData.isoInfo?.name} • {dealsData.isoInfo?.email}
                      </p>
                    </div>
                    <div>
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={refetch}
                        disabled={loading}
                        style={{ fontSize: '0.8rem' }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            Refresh
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

      {/* Stats Cards */}
      <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-4 px-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <i className="bi bi-graph-up text-primary" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {dealsData.opportunities?.length || 0}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total Deals</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-4 px-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <i className="bi bi-send text-success" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {dealsData.opportunities?.reduce((total, opp) => total + opp.submissions.length, 0) || 0}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total Submissions</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-4 px-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <i className="bi bi-currency-dollar text-warning" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {formatCurrency(dealsData.opportunities?.reduce((total, opp) => total + opp.amount, 0) || 0)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total Value</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-4 px-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <i className="bi bi-check-circle text-info" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {dealsData.opportunities?.filter(opp => opp.submissions.some(sub => sub.status === 'approved')).length || 0}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Approved Deals</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deals Table */}
              <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="bi bi-list-ul me-2"></i>
                    Deal Opportunities
                  </h5>
                </div>
                <div className="card-body p-0">
                  {dealsData.opportunities && dealsData.opportunities.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#495057', borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ fontSize: '0.75rem', padding: '0.75rem', border: 'none', color: '#ffffff', fontWeight: '600' }}>BUSINESS NAME</th>
                            <th style={{ fontSize: '0.75rem', padding: '0.75rem', border: 'none', color: '#ffffff', fontWeight: '600' }}>AMOUNT</th>
                            <th style={{ fontSize: '0.75rem', padding: '0.75rem', border: 'none', color: '#ffffff', fontWeight: '600' }}>CLOSE DATE</th>
                            <th style={{ fontSize: '0.75rem', padding: '0.75rem', border: 'none', color: '#ffffff', fontWeight: '600' }}>CREATED</th>
                            <th style={{ width: '120px', fontSize: '0.75rem', padding: '0.75rem', border: 'none', color: '#ffffff', fontWeight: '600', textAlign: 'center' }}>SUBMISSIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dealsData.opportunities.map((opportunity) => (
                            <React.Fragment key={opportunity.id}>
                      <tr style={{ backgroundColor: '#ffffff' }} className="deal-row-hover">
                        <td style={{ padding: '0.75rem', border: 'none', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#212529', marginBottom: '2px' }}>
                              {opportunity.businessLegalName || opportunity.name}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              ID: {opportunity.id}
                            </small>
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', border: 'none', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                          <span style={{ fontWeight: '600', color: '#28a745', fontSize: '0.9rem' }}>
                            {formatCurrency(opportunity.amount)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', border: 'none', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            {opportunity.closeDate ? formatDate(opportunity.closeDate) : 'Not set'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', border: 'none', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            {formatDate(opportunity.createdDate)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', border: 'none', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle', textAlign: 'center' }}>
                          <button
                            className={`btn ${expandedDeals.has(opportunity.id) ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                            onClick={() => toggleDeal(opportunity.id)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            <i className={`bi ${expandedDeals.has(opportunity.id) ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                            {opportunity.submissions.length} Submission{opportunity.submissions.length !== 1 ? 's' : ''}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Submissions */}
                      {expandedDeals.has(opportunity.id) && (
                        <tr>
                          <td colSpan={5} style={{ backgroundColor: '#f5f5f5', padding: '0', border: 'none' }}>
                            <div style={{ padding: '0.5rem', marginLeft: '0.5rem', marginRight: '0.5rem' }}>
                              {opportunity.submissions.length > 0 ? (
                                <div className="submissions-list">
                                  {opportunity.submissions.map((submission, index) => (
                                    <div 
                                      key={submission.id} 
                                      className="submission-row d-flex justify-content-between align-items-center py-1 px-2 mb-1"
                                      style={{ 
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#e9ecef',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '3px',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <div className="flex-grow-1">
                                        <div style={{ fontWeight: '600', fontSize: '0.75rem', marginBottom: '2px' }}>
                                          {submission.lenderName || `Lender ${submission.lenderId}`}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                          {formatDate(submission.createdDate)}
                                        </small>
                                      </div>
                                      <div>
                                        {getSubmissionStatusBadge(submission.status)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-3">
                                  <i className="bi bi-inbox text-muted" style={{ fontSize: '1.5rem' }}></i>
                                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                                    No submissions yet for this opportunity
                                  </p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-graph-up text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 mb-2" style={{ fontSize: '1.1rem', fontWeight: '600' }}>No Deals Found</h5>
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                This ISO hasn't submitted any deals yet.
              </p>
            </div>
          )}
        </div>
      </div>

                <style>{`
                  .deal-row-hover:hover {
                    background-color: #f8f9fa !important;
                  }
                  .card {
                    transition: all 0.2s ease-in-out;
                  }
                  .card:hover {
                    transform: translateY(-1px);
                  }
                `}</style>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ISODetailPage
